import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/http';

export interface PackageItem {
  id: string;
  name: string;
  /** Opcionais (ADR-054): só com as três a embalagem entra na escolha automática. */
  lengthCm: string | null;
  widthCm: string | null;
  heightCm: string | null;
  cost: string;
  active: boolean;
  /** Produtos fixados nesta embalagem (a escolha automática não conta). */
  productCount?: number;
  /** Repetida: outra ativa, mais antiga, tem as mesmas medidas (em qualquer posição). */
  duplicateOf?: { id: string; name: string } | null;
}

export type PackageInput = Omit<PackageItem, 'id' | 'productCount' | 'duplicateOf'>;

export interface AdditionalCostItem {
  id: string;
  name: string;
  kind: 'FIXED_PER_UNIT' | 'PERCENT_OF_PRICE';
  value: string;
  active: boolean;
}

export type Rounding = 'NO_ROUND' | 'ROUND_INTEGER' | 'ROUND_90' | 'ROUND_99';

export interface PricingSettings {
  targetMargin: string | null;
  minimumMargin: string | null;
  rounding: Rounding;
  packageClearanceCm: string;
  /** Fração: diferença de peso a partir da qual vira alerta (padrão 0.10). */
  weightTolerance: string;
  /** A Central de Ações deixa de fora os alertas de item sem estoque (padrão: sim). */
  hideOutOfStockFindings: boolean;
}

export const ROUNDING_LABELS: Record<Rounding, string> = {
  NO_ROUND: 'Sem arredondar',
  ROUND_INTEGER: 'Real inteiro (89,00)',
  ROUND_90: 'Final ,90 (89,90)',
  ROUND_99: 'Final ,99 (89,99)',
};

export const costKeys = {
  all: ['costs'] as const,
  packages: () => [...costKeys.all, 'packages'] as const,
  additional: () => [...costKeys.all, 'additional'] as const,
  settings: () => [...costKeys.all, 'settings'] as const,
};

export function usePackages() {
  return useQuery({
    queryKey: costKeys.packages(),
    queryFn: () => apiRequest<PackageItem[]>('/packages'),
  });
}

export function useAdditionalCosts() {
  return useQuery({
    queryKey: costKeys.additional(),
    queryFn: () => apiRequest<AdditionalCostItem[]>('/additional-costs'),
  });
}

export function usePricingSettings() {
  return useQuery({
    queryKey: costKeys.settings(),
    queryFn: () => apiRequest<PricingSettings>('/pricing-settings'),
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  // Custos mudam margem de todo produto: a análise no servidor refaz, e a tela recarrega.
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: costKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['products'] }),
      queryClient.invalidateQueries({ queryKey: ['findings'] }),
    ]);
}

export function useSavePackage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: PackageInput & { id?: string }) =>
      apiRequest<PackageItem>(id ? `/packages/${id}` : '/packages', {
        method: id ? 'PUT' : 'POST',
        body,
      }),
    onSuccess: invalidate,
  });
}

export function useSaveAdditionalCost() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: Omit<AdditionalCostItem, 'id'> & { id?: string }) =>
      apiRequest<{ id: string }>(id ? `/additional-costs/${id}` : '/additional-costs', {
        method: id ? 'PUT' : 'POST',
        body,
      }),
    onSuccess: invalidate,
  });
}

export function useSaveSettings() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: PricingSettings) =>
      apiRequest<void>('/pricing-settings', { method: 'PUT', body }),
    onSuccess: invalidate,
  });
}

export function useSetProductPackage(productId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (packageId: string | null) =>
      apiRequest<void>(`/products/${productId}/package`, { method: 'PATCH', body: { packageId } }),
    onSuccess: invalidate,
  });
}

/** Exclui a embalagem; com produtos fixados, move-os para `replaceWith` antes. */
export function useDeletePackage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, replaceWith }: { id: string; replaceWith?: string }) =>
      apiRequest<{ moved: number }>(
        `/packages/${id}${replaceWith ? `?${new URLSearchParams({ substituirPor: replaceWith }).toString()}` : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: invalidate,
  });
}

/** Embalagem que a escolha automática usa no produto, com as medidas que valeram (ADR-083). */
export interface AutomaticPackage {
  measures: { lengthCm: string; widthCm: string; heightCm: string } | null;
  choice:
    | {
        kind: 'FOUND';
        package: {
          id: string;
          name: string;
          lengthCm: string | null;
          widthCm: string | null;
          heightCm: string | null;
          cost: string;
        };
      }
    | { kind: 'NO_MEASURES' | 'NO_FIT' };
}

export function useAutomaticPackage(productId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...costKeys.all, 'automatic', productId],
    queryFn: () => apiRequest<AutomaticPackage>(`/products/${productId}/package/automatic`),
    enabled,
  });
}
